/**
 * Vesper API Client (Shared / Cross-Platform)
 *
 * Platform-agnostic Axios instance. Storage and auth failure handling
 * are injected at app startup via initStorage() and setOnAuthFailure().
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getApiBase } from '../constants/api';
import { getStorage, getOnAuthFailure, resolveStorageValue } from '../storage/storage';

const apiClient: AxiosInstance = axios.create({
  // Per-method headers: GET/HEAD/DELETE skip Content-Type so CORS preflight
  // isn't triggered for simple cross-origin reads. POST/PUT/PATCH still send
  // Content-Type when a body is attached. This was the cause of the demo
  // picker hang in 2026-05 — browser preflight cache was stalling axios GETs
  // (which carried Content-Type by default) while native fetch (no
  // Content-Type) worked instantly.
  headers: {
    common: {},
    post:   { 'Content-Type': 'application/json' },
    put:    { 'Content-Type': 'application/json' },
    patch:  { 'Content-Type': 'application/json' },
  },
  timeout: 15_000,
});

// ── Request Interceptor — inject JWT token ────────────────────────────────────
apiClient.interceptors.request.use(async (config) => {
  // Set baseURL dynamically (allows runtime configuration)
  config.baseURL = getApiBase();

  const storage = getStorage();
  const token = await resolveStorageValue(storage.getItem('ark_token'));
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/** Decode the unverified JWT payload to inspect claims (demo flag etc).
 *  We only use this to route the post-401 UX — never to authorize. The
 *  server is the source of truth for demo status. */
function decodeJwtClaims(token: string): { demo?: boolean } | null {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    // base64url → base64 → JSON. atob is available on web; for RN, the
    // Buffer polyfill from @vesper/shared startup handles it. If neither
    // is around, fall through and treat as non-demo.
    const norm = seg.replace(/-/g, '+').replace(/_/g, '/');
    const padded = norm + '='.repeat((4 - (norm.length % 4)) % 4);
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : (Buffer as { from: (s: string, e: string) => { toString: (e: string) => string } } | undefined)
            ?.from(padded, 'base64')
            .toString('utf8') ?? '';
    return json ? (JSON.parse(json) as { demo?: boolean }) : null;
  } catch {
    return null;
  }
}

// ── Response Interceptor — handle auth errors ─────────────────────────────────
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const storage = getStorage();
      const token = await resolveStorageValue(storage.getItem('ark_token'));
      // Detect demo-session expiry BEFORE clearing the token. The 4h JWT
      // hard cap and the 60min idle expiry both surface as 401 here. If
      // the expiring token had demo:true, route the user back to /demo
      // with a transient sessionStorage flag so DemoPicker can surface
      // a "your demo session ended — start a new one" toast instead of
      // dumping them on the login screen with no context.
      const claims = token ? decodeJwtClaims(token) : null;
      const wasDemo = claims?.demo === true;

      await Promise.resolve(storage.removeItem('ark_token'));

      // Prevent infinite redirect if already on the login page
      const win = typeof globalThis !== 'undefined' ? (globalThis as Record<string, unknown>)['window'] as { location?: { pathname?: string; href?: string }; sessionStorage?: { setItem: (k: string, v: string) => void } } | undefined : undefined;
      const pathname = win?.location?.pathname;
      const isOnLogin = pathname === '/login' || pathname === '/register';
      const isOnDemo = pathname === '/demo';

      if (wasDemo && !isOnDemo) {
        // Stash a flag DemoPicker reads on mount + redirect to /demo.
        try {
          win?.sessionStorage?.setItem('vesper.demo.expired', '1');
        } catch { /* best-effort */ }
        if (win?.location) {
          win.location.href = '/demo';
        }
      } else if (!isOnLogin) {
        const onAuthFailure = getOnAuthFailure();
        if (onAuthFailure) onAuthFailure();
      }
    }

    // Closed-beta gate (mig 068): a 403 with `accountStatus` in the payload
    // means the user is waitlisted (or rejected) and hit a founder-product
    // surface. Send them to the holding screen instead of letting the calling
    // component throw a generic "forbidden" error.
    if (error.response?.status === 403 && error.response?.data?.accountStatus) {
      const win = typeof globalThis !== 'undefined' ? (globalThis as Record<string, unknown>)['window'] as { location?: { pathname?: string; href?: string } } | undefined : undefined;
      const pathname = win?.location?.pathname;
      // Don't bounce when already on the holding page or auth pages.
      const isOnGate =
        pathname === '/waitlist' ||
        pathname === '/login' ||
        pathname === '/register' ||
        pathname === '/onboarding';
      if (!isOnGate && win?.location) {
        win.location.href = '/waitlist';
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
